// @ts-check
import { initSchema } from '@aws-amplify/datastore';
import { schema } from './schema';

const LmDistanceUnits = {
  FEET: 'Feet',
  YARDS: 'Yards',
  METERS: 'Meters'
};

const LmSpeedUnits = {
  MPH: 'Mph',
  KPH: 'Kph',
  MPS: 'Mps'
};

const LmTemperatureUnits = {
  FAHRENHEIT: 'Fahrenheit',
  CELCIUS: 'Celcius'
};

const LmDisplayMode = {
  GRAPHIC: 'Graphic',
  TILES: 'Tiles',
  MULTI_DATA: 'MultiData',
  SINGLE_DATA: 'SingleData'
};

const LmTheme = {
  LIGHT: 'Light',
  DARK: 'Dark'
};

const LmTargetOption = {
  NONE: 'None',
  BASKET: 'Basket',
  GREEN: 'Green',
  CIRCLE: 'Circle'
};

const LmCameraView = {
  STATIONARY: 'Stationary',
  BALL: 'Ball',
  FLIGHT: 'Flight'
};

const LmBallPath = {
  SIMULATED: 'Simulated',
  RAW: 'Raw'
};

const LmBallType = {
  PREMIUM: 'Premium',
  TITLEIST_RCT: 'TitleistRct'
};

const LmHandedness = {
  LEFT: 'Left',
  RIGHT: 'Right'
};

const DataType = {
  INT: 'Int',
  FLOAT: 'Float'
};

const LmLocation = {
  NET: 'Net',
  SCREEN: 'Screen',
  SIMULATOR: 'Simulator',
  OUTDOOR_RANGE: 'OutdoorRange',
  INDOOR_RANGE: 'IndoorRange',
  COURSE: 'Course',
  FIELD: 'Field'
};

const ValueType = {
  CLUB_SPEED: 'ClubSpeed',
  BALL_SPEED: 'BallSpeed',
  SMASH_FACTOR: 'SmashFactor',
  ATTACK_ANGLE: 'AttackAngle',
  CLUB_PATH: 'ClubPath',
  LAUNCH_ANGLE: 'LaunchAngle',
  HORIZONTAL_LAUNCH_ANGLE: 'HorizontalLaunchAngle',
  FACE_ANGLE: 'FaceAngle',
  FACE_TO_PATH: 'FaceToPath',
  SPIN_RATE: 'SpinRate',
  SPIN_AXIS: 'SpinAxis',
  CARRY_DISTANCE: 'CarryDistance',
  TOTAL_DISTANCE: 'TotalDistance',
  SIDE: 'Side',
  SIDE_TOTAL: 'SideTotal',
  APEX: 'Apex',
  BALL_DIRECTION: 'BallDirection',
  BALL_CURVE: 'BallCurve',
  DESCENT_ANGLE: 'DescentAngle'
};

const BaseballValueType = {
  PITCH_RELEASE_TIMESTAMP: 'pitchReleaseTimestamp',
  PITCH_RELEASE_WORLD3D_POSITION_METERS: 'pitchReleaseWorld3dPositionMeters',
  PITCH_RELEASE_VELOCITY_MPS: 'pitchReleaseVelocityMps',
  PITCH_RELEASE_ARM_SLOT_DEG: 'pitchReleaseArmSlotDeg',
  PITCH_RELEASE_HORIZONTAL_ANGLE_DEG: 'pitchReleaseHorizontalAngleDeg',
  PITCH_RELEASE_VERTICAL_ANGLE_DEG: 'pitchReleaseVerticalAngleDeg',
  PITCH_RELEASE_BACKWARD_EXTENSION_METERS: 'pitchReleaseBackwardExtensionMeters',
  PITCH_BREAK_HORIZONTAL_METERS: 'pitchBreakHorizontalMeters',
  PITCH_BREAK_VERTICAL_METERS: 'pitchBreakVerticalMeters',
  PITCH_BREAK_INDUCED_VERTICAL_METERS: 'pitchBreakInducedVerticalMeters',
  PITCH_SPIN_TOTAL_RPM: 'pitchSpinTotalRpm',
  PITCH_SPIN_ACTIVE_RPM: 'pitchSpinActiveRpm',
  PITCH_SPIN_BACK_RPM: 'pitchSpinBackRpm',
  PITCH_SPIN_SIDE_RPM: 'pitchSpinSideRpm',
  PITCH_SPIN_TOP_RPM: 'pitchSpinTopRpm',
  PITCH_SPIN_DIRECTION_CLOCK_BEARING: 'pitchSpinDirectionClockBearing',
  PITCH_SPIN_DIRECTION_EFFICIENCY_PCT: 'pitchSpinDirectionEfficiencyPct',
  PITCH_APPROACH_VELOCITY_MPS: 'pitchApproachVelocityMps',
  PITCH_APPROACH_PLATE_TIMESTAMP: 'pitchApproachPlateTimestamp',
  PITCH_APPROACH_PLATE_WORLD3D_POSITION_METERS: 'pitchApproachPlateWorld3dPositionMeters',
  PITCH_CROSS_PLATE_TIMESTAMP: 'pitchCrossPlateTimestamp',
  PITCH_CROSS_PLATE_WORLD3D_POSITION_METERS: 'pitchCrossPlateWorld3dPositionMeters',
  HIT_DIRECTION_DEG: 'hitDirectionDeg',
  HIT_DISTANCE_METERS: 'hitDistanceMeters',
  HIT_EXIT_VELOCITY_MPS: 'hitExitVelocityMps',
  HIT_LAUNCH_ANGLE_DEG: 'hitLaunchAngleDeg',
  HIT_SPIN_TOTAL_RPM: 'hitSpinTotalRpm',
  HIT_SPIN_ACTIVE_RPM: 'hitSpinActiveRpm',
  HIT_SPIN_BACK_RPM: 'hitSpinBackRpm',
  HIT_SPIN_SIDE_RPM: 'hitSpinSideRpm',
  HIT_SPIN_TOP_RPM: 'hitSpinTopRpm',
  HIT_SPIN_DIRECTION_CLOCK_BEARING: 'hitSpinDirectionClockBearing',
  HIT_SPIN_DIRECTION_EFFICIENCY_PCT: 'hitSpinDirectionEfficiencyPct',
  HIT_BALL_CONTACT_TIMESTAMP: 'hitBallContactTimestamp',
  HIT_BALL_CONTACT_WORLD3D_POSITION_METERS: 'hitBallContactWorld3dPositionMeters',
  BAT_SPEED_MPS: 'batSpeedMps',
  BAT_ROTATIONAL_ACCELERATION_GS: 'batRotationalAccelerationGs',
  BAT_ATTACK_ANGLE_DEG: 'batAttackAngleDeg',
  BAT_EARLY_CONNECTION_DEG: 'batEarlyConnectionDeg',
  BAT_CONNECTION_AT_IMPACT_DEG: 'batConnectionAtImpactDeg',
  BAT_VERTICAL_ANGLE_DEG: 'batVerticalAngleDeg',
  BAT_SWING_START_TIMESTAMP: 'batSwingStartTimestamp',
  BAT_PEAK_HAND_SPEED_MPS: 'batPeakHandSpeedMps',
  TEED_BALL_LOCATION_WORLD3D_POSITION_METERS: 'teedBallLocationWorld3dPositionMeters'
};

const LmGolfTrajectoryType = {
  FLIGHT: 'Flight',
  NORMALIZED: 'Normalized'
};

const LmBaseballTrajectoryType = {
  UNKNOWN: 'Unknown',
  PITCH: 'Pitch',
  HIT: 'Hit'
};

const LmClubType = {
  UNKNOWN: 'Unknown',
  DRIVER: 'Driver',
  WOOD2: 'Wood2',
  WOOD3: 'Wood3',
  WOOD4: 'Wood4',
  WOOD5: 'Wood5',
  WOOD6: 'Wood6',
  WOOD7: 'Wood7',
  WOOD8: 'Wood8',
  WOOD9: 'Wood9',
  IRON1: 'Iron1',
  IRON2: 'Iron2',
  IRON3: 'Iron3',
  IRON4: 'Iron4',
  IRON5: 'Iron5',
  IRON6: 'Iron6',
  IRON7: 'Iron7',
  IRON8: 'Iron8',
  IRON9: 'Iron9',
  PITCHING_WEDGE: 'PitchingWedge',
  SAND_WEDGE: 'SandWedge',
  LOB_WEDGE: 'LobWedge',
  APPROACH_WEDGE: 'ApproachWedge',
  GAP_WEDGE: 'GapWedge',
  WEDGE46: 'Wedge46',
  WEDGE48: 'Wedge48',
  WEDGE50: 'Wedge50',
  WEDGE52: 'Wedge52',
  WEDGE54: 'Wedge54',
  WEDGE56: 'Wedge56',
  WEDGE58: 'Wedge58',
  WEDGE60: 'Wedge60',
  WEDGE62: 'Wedge62',
  WEDGE64: 'Wedge64',
  PUTTER: 'Putter',
  HYBRID: 'Hybrid',
  HYBRID1: 'Hybrid1',
  HYBRID2: 'Hybrid2',
  HYBRID3: 'Hybrid3',
  HYBRID4: 'Hybrid4',
  HYBRID5: 'Hybrid5',
  HYBRID6: 'Hybrid6',
  HYBRID7: 'Hybrid7',
  HYBRID8: 'Hybrid8',
  HYBRID9: 'Hybrid9',
  OTHER: 'Other'
};

const LmClubCategory = {
  ALL: 'All',
  DRIVERS: 'Drivers',
  WOODS: 'Woods',
  HYBRIDS: 'Hybrids',
  IRONS: 'Irons',
  WEDGES: 'Wedges'
};

const LmShotType = {
  OFF_TEE: 'OffTee',
  APPROACH: 'Approach',
  AROUND_GREEN: 'AroundGreen',
  PUTT: 'Putt'
};

const LmShotShapeType = {
  STRAIGHT: 'Straight',
  FADE: 'Fade',
  DRAW: 'Draw'
};

const LmShotTrajectoryType = {
  HIGH: 'High',
  NORMAL: 'Normal',
  LOW: 'Low'
};

const LmDrillTargetType = {
  DISTANCE: 'Distance',
  SHOT_SHAPING: 'ShotShaping',
  UP_DOWN: 'UpDown'
};

const DateResolution = {
  DAY: 'Day',
  WEEK: 'Week',
  MONTH: 'Month'
};

const LmSportType = {
  GOLF: 'Golf',
  BASEBALL: 'Baseball'
};

const { LmUser, LmProfile, LmFeature, LmDevice, LmTeamPlayers, LmTeamCoaches, LmTeam, LmSession, LmShot, LmClub, LmDrillTemplate, LmDrill, LmShareSession, LmSessionStats, LmUserStats, LmDataResults, LmDataSession, BaseballBatDataPoints, BaseballPitchDataPoints, LmBaseballResults, LmBaseballStatistics, LmClockBearing, LmTimestamp, Lm2dPosition, Lm3dPosition, LmOrientation3d, LmPolynomial, LmTrajectory, LmShotNormalizedValues, LmDrillTarget, LmDistanceRange, LmDistanceTarget, LmShotShapeTarget, LmUpDownTarget, LmSessionData, LmShotData, ShotValues, ShotAverage, RangeCount, TimeWindowCount, ClubCount, ClubAverage, RegisterShadow, GraphQLResult, LmDataSessionListResult, LmDataResultsListResult } = initSchema(schema);

export {
  LmUser,
  LmProfile,
  LmFeature,
  LmDevice,
  LmTeamPlayers,
  LmTeamCoaches,
  LmTeam,
  LmSession,
  LmShot,
  LmClub,
  LmDrillTemplate,
  LmDrill,
  LmShareSession,
  LmSessionStats,
  LmUserStats,
  LmDataResults,
  LmDataSession,
  LmDistanceUnits,
  LmSpeedUnits,
  LmTemperatureUnits,
  LmDisplayMode,
  LmTheme,
  LmTargetOption,
  LmCameraView,
  LmBallPath,
  LmBallType,
  LmHandedness,
  DataType,
  LmLocation,
  ValueType,
  BaseballValueType,
  LmGolfTrajectoryType,
  LmBaseballTrajectoryType,
  LmClubType,
  LmClubCategory,
  LmShotType,
  LmShotShapeType,
  LmShotTrajectoryType,
  LmDrillTargetType,
  DateResolution,
  LmSportType,
  BaseballBatDataPoints,
  BaseballPitchDataPoints,
  LmBaseballResults,
  LmBaseballStatistics,
  LmClockBearing,
  LmTimestamp,
  Lm2dPosition,
  Lm3dPosition,
  LmOrientation3d,
  LmPolynomial,
  LmTrajectory,
  LmShotNormalizedValues,
  LmDrillTarget,
  LmDistanceRange,
  LmDistanceTarget,
  LmShotShapeTarget,
  LmUpDownTarget,
  LmSessionData,
  LmShotData,
  ShotValues,
  ShotAverage,
  RangeCount,
  TimeWindowCount,
  ClubCount,
  ClubAverage,
  RegisterShadow,
  GraphQLResult,
  LmDataSessionListResult,
  LmDataResultsListResult
};